import type { GeometryElement } from '../geometrypad.model';
import type { PlacedShape } from './placement.model';

/** Default gap between auto-placed shapes */
const AUTO_LAYOUT_GAP = 3;

/**
 * Auto-place elements that weren't placed by positional params.
 * Places them in a row to the right of existing elements with a fixed gap.
 */
export function autoLayoutUnplaced(
  elements: GeometryElement[],
  placedShapes: Map<string, PlacedShape>,
  canonicalVertices: Map<string, Record<string, [number, number]>>,
): void {
  // Find the rightmost x coordinate of all currently placed shapes
  let maxX = 0;
  for (const placed of placedShapes.values()) {
    for (const vertex of Object.values(placed.vertices)) {
      const coord = typeof vertex === 'function' ? vertex() : vertex;
      maxX = Math.max(maxX, coord[0]);
    }
  }

  for (const element of elements) {
    if (placedShapes.has(element.id)) {
      continue;
    }

    // Skip annotation elements - they depend on other elements
    if (element.type === 'LengthAnnotation' || element.type === 'ArrowAnnotation') {
      continue;
    }

    const canonical = canonicalVertices.get(element.id);
    if (!canonical || Object.keys(canonical).length === 0) {
      continue;
    }

    // Compute the bounding box of this shape in canonical coords
    let localMinX = Infinity;
    let localMaxX = -Infinity;
    for (const coord of Object.values(canonical)) {
      localMinX = Math.min(localMinX, coord[0]);
      localMaxX = Math.max(localMaxX, coord[0]);
    }

    // Offset to place it after the rightmost existing shape
    const offsetX = maxX + AUTO_LAYOUT_GAP - localMinX;

    const worldVertices: Record<string, [number, number]> = {};
    for (const [name, coord] of Object.entries(canonical)) {
      worldVertices[name] = [coord[0] + offsetX, coord[1]];
    }

    placedShapes.set(element.id, {
      elementId: element.id,
      type: element.type,
      vertices: worldVertices,
      transform: { rotation: 0, translation: [offsetX, 0] },
      isReactive: false,
    });

    // Update maxX for the next unplaced shape
    for (const coord of Object.values(worldVertices)) {
      maxX = Math.max(maxX, coord[0]);
    }
  }
}
